/* ============================================================================
   HXP — HESTERN PDP STORY SECTIONS · behaviour
   ----------------------------------------------------------------------------
   Loaded once per page (the browser de-dupes the identical <script src>, the
   same way Dawn loads per-section assets) and guarded so a second execution is
   a no-op. Everything is idempotent and re-runnable, because the theme editor
   re-injects a section's HTML on every settings change and fires
   `shopify:section:load` — an init that assumed "first run" would double the
   marquee's items a little more each time a merchant nudged a colour.

   FOUR THINGS THAT LOOK ODD AND ARE LOAD-BEARING

   1. `hxp-js` IS SET BY snippets/hxp-assets.liquid, NOT HERE.
      The reveal styles only arm themselves under `.hxp-js`. If this file set
      that class it would land AFTER first paint, so every section would flash
      in at full opacity and then jump back to hidden before animating. The
      snippet sets it inline, at parse time, before any section markup exists.
      This file's job is only to take it back off if it turns out we cannot
      observe anything (see 2).

   2. THE FAILSAFE REVEALS WHAT IS ON SCREEN, NOT EVERYTHING.
      `.hxp-js .hxp-reveal { opacity: 0 }` means any path where the observer
      never fires leaves the section INVISIBLE, not merely un-animated — a
      silent, total content loss. So there is a timer. But a timer that reveals
      EVERY element on the page also destroys the feature it is protecting:
      1.6 seconds in, all six sections would be pre-revealed and nothing would
      ever animate on scroll. It reveals only what is already at or above the
      fold — which is exactly what a healthy observer has already done, making
      it a no-op in the normal case.

      Whether the observer is healthy is knowable: IntersectionObserver
      delivers an initial callback for every target it is handed, intersecting
      or not. So if `ioFired` is still false after that timer, the observer is
      genuinely not delivering, and a throttled scroll listener takes over for
      the rest of the page. That listener is never attached when things work.

   3. VIDEOS CARRY NO `autoplay` ATTRIBUTE AND `preload="none"`.
      Both clips together are ~7.3 MB. With `autoplay`, Chrome fetches them at
      page load even under `preload="none"` — on a product page that is 7 MB
      spent before the shopper has scrolled past the buy button. Playback is
      started from an observer instead, so the bytes are only spent if the
      section is actually reached. No-JS falls back to the poster frame, which
      is the video's own first frame.

   4. THE MARQUEE IS TOPPED UP TO AN EVEN NUMBER OF SETS.
      The CSS loop translates the track by exactly -50%, which is only seamless
      while the track is two identical halves. Liquid prints the list twice; if
      one pass is narrower than the viewport there would be a visible gap, so
      this file re-clones in PAIRS. Cloning an odd number of times would break
      the -50% and make the marquee visibly jump once per cycle.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__hxpPdpInit) return;
  window.__hxpPdpInit = true;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function each(list, fn) { Array.prototype.forEach.call(list || [], fn); }
  function qsa(sel, root) { return (root || document).querySelectorAll(sel); }

  /* ==========================================================================
     1 · SCROLL REVEAL
     ========================================================================== */
  var revealObserver = null;
  var ioFired = false;      /* has the observer delivered ANY callback yet? */
  var fallbackBound = false;

  function revealAll(root) {
    each(qsa('.hxp-reveal', root || document), function (el) { el.classList.add('hxp-in'); });
  }

  /* Reveal only what the viewport has already reached. `top < vh` covers both
     what is on screen and everything scrolled past above it. */
  function revealVisible(root) {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    each(qsa('.hxp-reveal:not(.hxp-in)', root || document), function (el) {
      if (el.getBoundingClientRect().top < vh) el.classList.add('hxp-in');
    });
  }

  function initReveal(root) {
    var els = qsa('.hxp-reveal:not(.hxp-in)', root || document);
    if (!els.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      each(els, function (el) { el.classList.add('hxp-in'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        ioFired = true;
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          en.target.classList.add('hxp-in');
          revealObserver.unobserve(en.target);
        });
      }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });
    }
    each(els, function (el) { revealObserver.observe(el); });
  }

  /* See note 2. An unrevealed element is an INVISIBLE element, never just a
     static one — so this runs no matter what happened above. */
  function armFailsafe(root) {
    window.setTimeout(function () {
      revealVisible(root);
      if (ioFired || fallbackBound || !('IntersectionObserver' in window)) return;

      /* The observer exists but has delivered nothing, which it should have
         done for every target the moment it was handed one. Drive the reveal
         from scroll instead, for the rest of the page's life. */
      fallbackBound = true;
      var pending = false;
      window.addEventListener('scroll', function () {
        if (pending) return;
        pending = true;
        window.setTimeout(function () { revealVisible(document); pending = false; }, 120);
      }, { passive: true });
      revealVisible(document);
    }, 1600);
  }

  /* ==========================================================================
     2 · THE MAKING — scroll-driven progress timeline
     The dot rests at the reading line (~48% down the viewport) while the steps
     scroll under it, so it lands on step N while step N is on screen. The
     perceived speed is set by how far apart the steps are, not by any easing.
     ========================================================================== */
  function initTimeline(root) {
    each(qsa('.hxp-steps', root || document), function (steps) {
      var tl = steps.querySelector('.hxp-tl');
      if (!tl) return;

      var fill = steps.querySelector('.hxp-tl__fill');
      var dot = steps.querySelector('.hxp-tl__dot');
      var items = steps.querySelectorAll('.hxp-step');
      if (!fill || !dot || items.length < 2) return;

      var nums = [];
      each(items, function (it) { nums.push(it.querySelector('.hxp-no')); });
      if (nums.indexOf(null) !== -1) return;

      var ticking = false;

      /* Snap the rail's start/end to the centre of the FIRST and LAST step
         numerals, so the dot lands exactly on each marker at any width. */
      function layout() {
        var sb = steps.getBoundingClientRect();
        var a = nums[0].getBoundingClientRect();
        var b = nums[nums.length - 1].getBoundingClientRect();
        var top = a.top + a.height / 2 - sb.top;
        var bot = b.top + b.height / 2 - sb.top;
        tl.style.top = top + 'px';
        tl.style.bottom = 'auto';
        tl.style.height = Math.max(1, bot - top) + 'px';
      }

      function update() {
        var r = tl.getBoundingClientRect();
        if (!r.height) return;
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var p = (vh * 0.48 - r.top) / r.height;
        p = Math.max(0, Math.min(1, p));
        fill.style.height = p * 100 + '%';
        dot.style.top = p * 100 + '%';

        var dotY = r.top + p * r.height;
        for (var i = 0; i < nums.length; i++) {
          var nr = nums[i].getBoundingClientRect();
          items[i].classList.toggle('hxp-active', nr.top + nr.height / 2 <= dotY + 2);
        }
      }

      /* rAF is the right place to do this work, but it is not guaranteed to
         run: a background tab, a hidden embed or a throttled renderer can
         starve it indefinitely. A plain `if (ticking) return` lock would then
         latch on the very first scroll and the rail would freeze for the rest
         of the session — with no error to notice. Both callbacks race; the
         first to arrive clears the flag and the loser becomes a no-op. Clearing
         BEFORE update() also means a throw in update() cannot latch the lock. */
      function onScroll() {
        if (ticking) return;
        ticking = true;
        var run = function () {
          if (!ticking) return;
          ticking = false;
          update();
        };
        window.requestAnimationFrame(run);
        window.setTimeout(run, 60);
      }

      function relayout() { layout(); update(); }

      if (steps.__hxpTl) {
        window.removeEventListener('scroll', steps.__hxpTl.scroll);
        window.removeEventListener('resize', steps.__hxpTl.resize);
      }
      steps.__hxpTl = { scroll: onScroll, resize: relayout };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', relayout);
      window.addEventListener('load', relayout);
      /* Web fonts land after first layout and change the numerals' box, which
         moves the rail's endpoints. Re-measure once they are in. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(relayout).catch(function () {});
      }
      relayout();
    });
  }

  /* ==========================================================================
     3 · MARQUEE — keep the track at an even number of full sets (see note 4)
     ========================================================================== */
  function initMarquee(root) {
    each(qsa('.hxp-marquee', root || document), function (mq) {
      var track = mq.querySelector('.hxp-marquee__track');
      if (!track) return;

      /* Re-runnable: always rebuild from the ORIGINAL set, which is cached on
         the node the first time we see it. */
      if (!track.__hxpSet) {
        var half = Math.floor(track.children.length / 2) || track.children.length;
        var set = [];
        for (var i = 0; i < half; i++) set.push(track.children[i].cloneNode(true));
        track.__hxpSet = set;
      }
      var set = track.__hxpSet;
      if (!set.length) return;

      function build(pairs) {
        var frag = document.createDocumentFragment();
        for (var p = 0; p < pairs * 2; p++) {
          for (var i = 0; i < set.length; i++) frag.appendChild(set[i].cloneNode(true));
        }
        track.innerHTML = '';
        track.appendChild(frag);
      }

      build(1);
      /* One full pass must be at least as wide as the marquee, or the -50%
         translate exposes empty space at the wrap point. */
      var need = mq.getBoundingClientRect().width || window.innerWidth;
      var pairs = 1;
      while (track.scrollWidth / 2 < need && pairs < 8) {
        pairs += 1;
        build(pairs);
      }
    });
  }

  /* ==========================================================================
     4 · VIDEOS — play on approach, pause when gone (see note 3)
     ========================================================================== */
  var videoObserver = null;

  function playVideo(v) {
    if (v.getAttribute('data-hxp-playing') === '1') return;
    v.setAttribute('data-hxp-playing', '1');
    v.muted = true;                 /* required for a programmatic play() */
    v.setAttribute('muted', '');
    var p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () { v.removeAttribute('data-hxp-playing'); });
    }
  }

  function initVideos(root) {
    var vids = qsa('video[data-hxp-video]', root || document);
    if (!vids.length) return;

    if (REDUCED) return;            /* poster frame only — the CSS agrees */

    if (!('IntersectionObserver' in window)) {
      each(vids, playVideo);
      return;
    }

    if (!videoObserver) {
      videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var v = en.target;
          if (en.isIntersecting) {
            playVideo(v);
          } else if (!v.paused) {
            v.pause();
            v.removeAttribute('data-hxp-playing');
          }
        });
      }, { rootMargin: '300px 0px 300px 0px', threshold: 0 });
    }
    each(vids, function (v) { videoObserver.observe(v); });
  }

  /* ==========================================================================
     5 · BOOT
     ========================================================================== */
  function init(root) {
    initReveal(root);
    initTimeline(root);
    initMarquee(root);
    initVideos(root);
    armFailsafe(root);
  }

  function boot() { init(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor: a settings change re-renders one section's HTML in place. */
  document.addEventListener('shopify:section:load', function (e) {
    var t = e.target;
    if (!t || !t.querySelector || !t.querySelector('.hxp')) return;
    init(t);
  });

  /* Re-measure the marquee when the layout width changes (orientation, editor
     preview width, a sidebar opening). Debounced — scrollWidth reads force a
     reflow and this can fire dozens of times per drag. */
  var rt = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(rt);
    rt = window.setTimeout(function () { initMarquee(document); }, 220);
  });
})();
